import { REPEAT_MODE } from 'features/schedulingEngine/constants/repeatTypes'
import { mapRepeatToApi } from 'features/schedulingEngine/adapters/mapRepeatToApi'
import { clampStudyDay } from '../constants'
import { resolveProjectionDayCap } from '../projectionDayCap'
import { getRecurringOccurrenceDays } from '../legacy/recurringExpansion'
import { getAfterNOccurrencesEndRuleCount, resolveEndRulesLastStudyDay } from './engineEndRules'
import { expandRruleStudyDays } from './rruleExpansion'

const isPersistentEngineSchedule = schedule => {
  const repeat = schedule?.repeat
  if (repeat?.mode === 'persistent') return true
  const recurring = schedule?.recurring
  if (recurring?.scheme === 'persistent') return true
  return false
}

const expandCountedDailyDays = (firstStudyDay, count, studyEndDay) => {
  const cap = resolveProjectionDayCap(studyEndDay)
  const start = clampStudyDay(firstStudyDay)
  const total = Math.floor(Number(count))
  if (!Number.isFinite(total) || total <= 0) return [start]
  const days = []
  for (let i = 0; i < total; i += 1) {
    const day = start + i
    if (day > cap) break
    days.push(day)
  }
  return days.length ? days : [start]
}

/** Expand V3 schedule to study days (single anchor day + repeat rules). */
export const expandEngineScheduleStudyDays = (
  firstStudyDay,
  schedule,
  context = {},
) => {
  const { useAbsoluteStartDate, absoluteStartDate, studyEndDay } = context
  const seriesEndStudyDay = resolveEndRulesLastStudyDay(schedule, context)

  if (isPersistentEngineSchedule(schedule)) {
    return getRecurringOccurrenceDays(firstStudyDay, { ...schedule, recurring: { scheme: 'persistent' } }, useAbsoluteStartDate, absoluteStartDate, studyEndDay)
  }

  const repeat = schedule?.repeat
  const endRuleCount = getAfterNOccurrencesEndRuleCount(schedule)
  if (!repeat || repeat.mode === REPEAT_MODE.doesNotRepeat || repeat.mode === 'does_not_repeat') {
    const recurring = schedule?.recurring
    if (recurring?.scheme && recurring.scheme !== 'persistent') {
      return getRecurringOccurrenceDays(
        firstStudyDay,
        schedule,
        useAbsoluteStartDate,
        absoluteStartDate,
        studyEndDay,
      )
    }
    return [clampStudyDay(firstStudyDay)]
  }

  if (repeat.mode === 'rrule') {
    return expandRruleStudyDays(
      firstStudyDay,
      repeat,
      studyEndDay,
      endRuleCount,
      seriesEndStudyDay,
    )
  }

  const rruleModes = [
    REPEAT_MODE.weeklyOnSelectedDays,
    REPEAT_MODE.everyNWeeks,
    REPEAT_MODE.everyNHours,
    REPEAT_MODE.everyNMinutes,
    REPEAT_MODE.everyNMonths,
    REPEAT_MODE.monthlyOnDay,
  ]
  if (rruleModes.includes(repeat.mode)) {
    const apiRepeat = mapRepeatToApi(repeat)
    if (apiRepeat.mode === 'rrule' && apiRepeat.rrule) {
      return expandRruleStudyDays(
        firstStudyDay,
        apiRepeat,
        studyEndDay,
        endRuleCount,
        seriesEndStudyDay,
      )
    }
  }

  if (repeat.mode === REPEAT_MODE.everyNDays || repeat.mode === 'every_n_days') {
    const legacyShape = {
      recurring: {
        scheme: 'n-days',
        interval: repeat.intervalDays ?? repeat.interval_days ?? 1,
        recurrences: repeat.recurrences ?? repeat.count ?? endRuleCount,
      },
      expire: {},
    }
    return getRecurringOccurrenceDays(
      firstStudyDay,
      legacyShape,
      useAbsoluteStartDate,
      absoluteStartDate,
      studyEndDay,
    )
  }

  const count = repeat.recurrences ?? repeat.count ?? endRuleCount
  if (count != null && count > 0) {
    return expandCountedDailyDays(firstStudyDay, count, studyEndDay)
  }

  return [clampStudyDay(firstStudyDay)]
}
